import React, { useState } from 'react';
import { gql, useMutation, useNavigate } from '@common/hooks';
import { Card, Form, Checkbox } from 'antd';
import { MessengerLauncherWrapper } from 'works/legacy/components/messenger-launcher-wrapper';
import { createMessage } from 'works/legacy/lib/message-helper';
import { Icon, SubmitButton } from '@common/components';
import { Colors } from '@common/tokens';
import { Spacing } from 'works/legacy/lib/style-helper';
import { TermsAndConditionsBody } from './account-settings';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { imagePath } from '@common/services/images-service';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useCurrentEntity } from '@identity/hooks';

const ACCEPT_TERMS_MUTATION = gql`
  mutation AcceptTermsConditions {
    acceptTermsConditions {
      organization {
        id
        termsConditionsAcceptedAt
      }
    }
  }
`;

export function TermsAndConditionsPage() {
  const [checked, setChecked] = useState(false);
  const [form] = Form.useForm();
  const [acceptTerms] = useMutation(ACCEPT_TERMS_MUTATION, {
    refetchQueries: [
      'currentWorksUser',
      'CurrentEntityQuery',
      'AccountSettingsCurrentWorksUser',
    ],
  });
  const navigate = useNavigate();
  const currentEntity = useCurrentEntity();

  useSetBreadcrumbs([
    { name: 'Terms and Conditions', path: '/works/terms-and-conditions' },
  ]);

  const onFinish = () => {
    acceptTerms().then((res) => {
      if (res.errors) {
        createMessage(
          'error',
          'Could not accept terms. Please contact support.'
        );
      } else {
        navigate('/works');
        createMessage(
          'success',
          'Terms and Conditions were successfully accepted.'
        );
      }
    });
  };

  const onChecked = (event: CheckboxChangeEvent) => {
    setChecked(event.target.checked);
  };

  return (
    <div className="TermsAndConditionsPage">
      <section style={{ textAlign: 'center' }}>
        <img
          src={imagePath('./works-logo-elm.svg')}
          alt="Trusted Works Logo"
          width="32px"
          style={{ marginBottom: `${Spacing.baseSpacing / 2}px` }}
        />
        <h2>Agree to Terms and Conditions</h2>
        <p>
          Please accept these terms and conditions for your team to start using
          Works.
        </p>
      </section>

      <Card className="terms-card">
        <TermsAndConditionsBody />
      </Card>

      {currentEntity.requiresAcceptTermsConditions && (
        <>
          <Checkbox checked={checked} onChange={onChecked}>
            I accept these Terms and Conditions on behalf of{' '}
            <strong>{currentEntity.name}</strong>.
          </Checkbox>

          <section
            style={{ marginTop: Spacing.baseSpacingPx, textAlign: 'center' }}
          >
            <Form form={form} onFinish={onFinish}>
              <Form.Item>
                <SubmitButton disabled={!checked}>Confirm</SubmitButton>
              </Form.Item>
            </Form>
          </section>

          <section style={{ textAlign: 'center' }}>
            <MessengerLauncherWrapper>
              <span className="helper-text">
                Need some help? <strong>Chat with Support</strong>{' '}
                <Icon icon="commentAltSmile" kind="light" size="md" />
              </span>
            </MessengerLauncherWrapper>
          </section>
        </>
      )}

      <style jsx>{`
        .TermsAndConditionsPage :global(.terms-card) {
          margin-bottom: ${Spacing.baseSpacingPx};
          height: 100%;
          max-height: calc(80vh - 250px);
          overflow-y: auto;
        }
        .TermsAndConditionsPage {
          max-width: 750px;
          margin: 0 auto;
          padding-top: ${Spacing.baseSpacingPx};
        }
        .TermsAndConditionsPage :global(.helper-text) {
          color: ${Colors.midnightLighter};
          font-weight: 400;
        }
        .TermsAndConditionsPage :global(.helper-text) strong {
          color: ${Colors.elm};
        }
      `}</style>
    </div>
  );
}
